import type { MotionStyle } from './styles';

/**
 * Outros: how the video ends, on top of any layer style and opener. Like openers they are made
 * of generated graphics and camera moves, timed from the end of the video (`beforeEnd`); they
 * can also send layers away and keep the ones that sell (product, price, button, logo). The
 * video grows by `span` so the ending never eats the time to look at the design.
 */
export const OUTROS: MotionStyle[] = [
  {
    id: 'outro-endcard',
    category: 'outro',
    label: [
      'End card',
      'Фінальна картка',
      'Plansza końcowa',
      'Cartelă finală',
      'Cartela final',
      'Финальная карточка',
    ],
    description: [
      'Colour panels sweep the design away and leave the call to action on a brand card',
      'Кольорові панелі змітають дизайн і лишають заклик на фірмовій картці',
      'Kolorowe panele zmiatają projekt i zostawiają wezwanie na planszy marki',
      'Panourile colorate mătură designul și lasă îndemnul pe o cartelă de brand',
      'Los paneles de color barren el diseño y dejan la llamada en una cartela de marca',
      'Цветные панели сметают дизайн и оставляют призыв на фирменной карточке',
    ],
    duration: 0,
    stagger: 0,
    span: 2600,
    fx: [
      {
        kind: 'title',
        beforeEnd: 2100,
        duration: 'rest',
        textFrom: ['cta', 'headline'],
        params: { style: 'slam', exit: 'cut', bg: 'accent', accentColor: 'dark' },
      },
      { kind: 'panels', beforeEnd: 2600, duration: 1100, params: { direction: 'left' } },
    ],
  },
  {
    id: 'outro-offer',
    category: 'outro',
    label: [
      'Offer focus',
      'Фокус на пропозиції',
      'Skupienie na ofercie',
      'Accent pe ofertă',
      'Foco en la oferta',
      'Фокус на предложении',
    ],
    description: [
      'The text leaves; the product, price, button and logo stay, the button beats',
      'Текст іде; товар, ціна, кнопка й логотип лишаються, кнопка пульсує',
      'Tekst znika; produkt, cena, przycisk i logo zostają, przycisk pulsuje',
      'Textul pleacă; produsul, prețul, butonul și logoul rămân, butonul pulsează',
      'El texto se va; el producto, el precio, el botón y el logo se quedan',
      'Текст уходит; товар, цена, кнопка и логотип остаются, кнопка пульсирует',
    ],
    duration: 0,
    stagger: 0,
    span: 2600,
    requires: 'cta',
    fallback: 'outro-endcard',
    leave: {
      preset: 'fade',
      duration: 500,
      keep: ['background', 'photo', 'price', 'cta', 'logo'],
    },
    roles: {
      cta: { emphasis: { preset: 'heartbeat', repeat: 0, gap: 500 } },
      price: { emphasis: { preset: 'pulse', repeat: 1 } },
    },
    fx: [{ kind: 'rings', beforeEnd: 2000, duration: 1300, anchor: 'cta', params: { count: 3 } }],
    camera: [{ preset: 'push-in', beforeEnd: 2600, duration: 2600, params: { intensity: 0.35 } }],
  },
  {
    id: 'outro-party',
    category: 'outro',
    label: ['Celebration', 'Свято', 'Święto', 'Sărbătoare', 'Celebración', 'Праздник'],
    description: [
      'A confetti burst, a punch on the price and a bouncing button',
      'Вибух конфеті, удар по ціні і кнопка, що підстрибує',
      'Wybuch konfetti, uderzenie w cenę i podskakujący przycisk',
      'O explozie de confetti, un impact pe preț și un buton care sare',
      'Estallido de confeti, golpe en el precio y un botón que salta',
      'Взрыв конфетти, удар по цене и подпрыгивающая кнопка',
    ],
    duration: 0,
    stagger: 0,
    span: 2400,
    roles: {
      cta: { emphasis: { preset: 'tada', repeat: 2, gap: 300 } },
      price: { emphasis: { preset: 'heartbeat', repeat: 1 } },
    },
    fx: [
      { kind: 'flash', beforeEnd: 2400, duration: 350, params: { opacity: 0.6 } },
      { kind: 'confetti', beforeEnd: 2400, duration: 2400, params: { mode: 'burst' } },
      {
        kind: 'burst',
        beforeEnd: 2300,
        duration: 800,
        anchor: 'price',
        params: { count: 14, size: 0.8 },
      },
    ],
    camera: [{ preset: 'punch', beforeEnd: 2400, duration: 450 }],
  },
  {
    id: 'outro-cinema',
    category: 'outro',
    label: [
      'Cinema ending',
      'Кінофінал',
      'Kinowe zakończenie',
      'Final de film',
      'Final de cine',
      'Кинофинал',
    ],
    description: [
      'Letterbox closes in, the camera pulls back and a dark curtain falls',
      'Кінорамка насувається, камера відʼїжджає, опускається темна завіса',
      'Kadr kinowy się zamyka, kamera odjeżdża, opada ciemna kurtyna',
      'Banda cinema se închide, camera se retrage, cade o cortină întunecată',
      'Las bandas se cierran, la cámara se aleja y cae un telón oscuro',
      'Кинорамка наезжает, камера отъезжает, опускается тёмный занавес',
    ],
    duration: 0,
    stagger: 0,
    span: 3000,
    fx: [
      // Late and thin: the price and the button stay readable while the camera pulls back
      {
        kind: 'letterbox',
        beforeEnd: 2000,
        duration: 'rest',
        params: { mode: 'hold', size: 7 },
      },
      {
        kind: 'panels',
        beforeEnd: 1100,
        duration: 'rest',
        params: { mode: 'cover', direction: 'down', count: 1, color: 'dark' },
      },
    ],
    camera: [{ preset: 'pull-out', beforeEnd: 3000, duration: 3000, params: { intensity: 0.5 } }],
  },
  {
    id: 'outro-loop',
    category: 'outro',
    label: ['Loop', 'Петля', 'Pętla', 'Buclă', 'Bucle', 'Петля'],
    description: [
      'Everything leaves together at the very end, so a looping banner starts over cleanly',
      'Усе йде разом у самому кінці, щоб банер-петля починався чисто',
      'Wszystko znika razem na końcu, by zapętlony baner zaczynał się czysto',
      'Totul pleacă împreună la final, ca bannerul în buclă să reînceapă curat',
      'Todo se va a la vez al final, para que el banner en bucle empiece limpio',
      'Всё уходит вместе в самом конце, чтобы баннер-петля начинался чисто',
    ],
    duration: 0,
    stagger: 0,
    span: 600,
    leave: { preset: 'fade', duration: 600, keep: ['background'] },
  },
];
